import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import glassTheme from '../../styles/glassTheme';

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  ${glassTheme.glass.heavy}
  box-shadow: ${glassTheme.shadow.xl};
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  background: rgba(0, 0, 0, 0.3);
  border-radius: ${glassTheme.borderRadius.lg} ${glassTheme.borderRadius.lg} 0 0;
`;

const ModalTitle = styled.h2`
  background: ${glassTheme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:before {
    content: '📊';
  }
`;

const CloseButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: ${glassTheme.colors.text.primary};
  font-size: 1.5rem;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(239, 68, 68, 0.3);
    border-color: ${glassTheme.colors.status.error};
    transform: rotate(90deg);
    box-shadow: 0 0 20px ${glassTheme.colors.status.error};
  }
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid ${glassTheme.colors.border};
  border-radius: 0 0 ${glassTheme.borderRadius.lg} ${glassTheme.borderRadius.lg};
  flex-shrink: 0;
`;

const NavButton = styled.button<{ direction: 'prev' | 'next' }>`
  padding: 0.6rem 1.2rem;
  background: ${glassTheme.gradients.primary};
  border: none;
  border-radius: ${glassTheme.borderRadius.lg};
  color: ${glassTheme.colors.text.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  ${props => props.direction === 'prev' && `
    &:before {
      content: '←';
    }
  `}

  ${props => props.direction === 'next' && `
    &:after {
      content: '→';
    }
  `}
`;

const PlotCounter = styled.div`
  color: ${glassTheme.colors.text.secondary};
  font-size: 1rem;
  font-weight: 500;
`;

const ModalBody = styled.div`
  padding: 2rem;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 600px;
  min-height: 400px;
`;

const PlotImage = styled.img`
  max-width: 100%;
  max-height: calc(90vh - 250px);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  background: white;
  object-fit: contain;
`;

interface PlotModalProps {
  isOpen: boolean;
  plots: string[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

export const PlotModal: React.FC<PlotModalProps> = ({
  isOpen,
  plots,
  currentIndex,
  onClose,
  onNext,
  onPrevious
}) => {
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onPrevious();
          break;
        case 'ArrowRight':
          onNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, onPrevious, onNext]);

  if (!isOpen || plots.length === 0) {
    return null;
  }

  return createPortal(
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>R 图表</ModalTitle>
          <CloseButton onClick={onClose} title="关闭 (ESC)">
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <PlotImage
            src={plots[currentIndex]}
            alt={`R Plot ${currentIndex + 1}`}
          />
        </ModalBody>

        {plots.length > 1 && (
          <Navigation>
            <NavButton
              direction="prev"
              onClick={onPrevious}
              disabled={currentIndex === 0}
            >
              上一张
            </NavButton>
            <PlotCounter>
              {currentIndex + 1} / {plots.length}
            </PlotCounter>
            <NavButton
              direction="next"
              onClick={onNext}
              disabled={currentIndex === plots.length - 1}
            >
              下一张
            </NavButton>
          </Navigation>
        )}
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
